import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";
import { MaintenanceSpecificationDirectiveKlass } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import MaintenanceSpecificationForm from "@/features/establishmentLeadsFunnel/components/maintenanceModule/maintenanceSpecification/MaintenanceSpecificationForm";
import { useCreateEstablishmentCompanyMaintenanceSpecification } from "@/features/establishments/hooks/maintenanceModels/useCreateEstablishmentCompanyMaintenanceSpecification";
import { maintenanceSpecificationSchema, MaintenanceSpecificationValidationSchema } from "@/features/establishments/schemas/maintenanceSpecification.schema";

interface CreateMaintenanceSpecificationModalProps {
  directiveKlass: MaintenanceSpecificationDirectiveKlass;
  establishmentCompany: EstablishmentCompanyData;
  alertText: {
    title: string;
    description: string;
  };
  disabled?: boolean;
}

const CreateMaintenanceSpecificationModal = ({
  directiveKlass,
  establishmentCompany,
  alertText,
  disabled,
}: CreateMaintenanceSpecificationModalProps) => {
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  const formMethods = useForm<MaintenanceSpecificationValidationSchema>({
    resolver: zodResolver(maintenanceSpecificationSchema),
    defaultValues: {
      directive_klass: directiveKlass,
    },
  });

  const {
    setError,
    handleSubmit,
    reset,
  } = formMethods;

  const {
    mutate: createMaintenanceSpecification,
    isPending: createMaintenanceSpecificationIsPending,
  } = useCreateEstablishmentCompanyMaintenanceSpecification({
    establishmentCompanyId: establishmentCompany.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany.id] });

      setOpen(false);
    },
    onError: FormTransformers.errorsTransformer<MaintenanceSpecificationValidationSchema>({
      setError,
    }),
  });

  const onSubmit: SubmitHandler<MaintenanceSpecificationValidationSchema> = (data) => createMaintenanceSpecification(data);

  useEffect(() => {
    if (open) {
      reset({
        directive_klass: directiveKlass,
      });
    }
  }, [open, directiveKlass]);

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Modal
        isOpen={open}
        setIsOpen={setOpen}
        triggerAsChild
        triggerDisabled={disabled}
        Trigger={(
          <Button
            className="py-2 px-4 text-sm font-normal"
            onClick={(event) => event.stopPropagation()}
            operationKlassToCheckPermission="Admin::Establishment::Companies::UpdateMaintenanceModels"
            disabled={disabled}
          >
            Adicionar
          </Button>
        )}
        title="Especificando atendimentos"
        useCancellableFooter
        confirmButtonClassName="bg-blue-700 rounded-br-lg"
        cancelButtonClassName="bg-white-200 rounded-bl-lg"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={createMaintenanceSpecificationIsPending}
        body={(
          <FormProvider {...formMethods}>
            <MaintenanceSpecificationForm
              alertText={alertText}
            />
          </FormProvider>
        )}
      />
    </div>
  );
}

export default CreateMaintenanceSpecificationModal;
